/**
 * StarForceModal.jsx - Star Force enhancement UI
 * MapleStory-style equipment enhancement with animation and effects
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { getEnhancementInfo, STAR_FORCE_CONFIG, calculateStarForceStats } from '../../modules/starForce';
import ItemChip from '../UI/ItemChip';
import './StarForceModal.css';

function StarForceModal({ item, onClose }) {
  const { playerCharacter, enhanceEquipment, triggerTutorial } = useGame();

  // Trigger tutorial on first open
  useEffect(() => {
    triggerTutorial('starForce');
  }, [triggerTutorial]);

  const [useSafeguard, setUseSafeguard] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceResult, setEnhanceResult] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(null); // 'charging', 'flash', 'result'

  // Track current star level (updates after each enhancement)
  const [currentStarLevel, setCurrentStarLevel] = useState(item?.starForce || 0);
  // Track consecutive failures for pity system
  const [consecutiveFailures, setConsecutiveFailures] = useState(item?.consecutiveFailures || 0);

  // Memoize currentItem to ensure fresh object when state changes
  // Also recalculate starForceStats based on current star level
  const currentItem = useMemo(() => ({
    ...item,
    starForce: currentStarLevel,
    starForceStats: calculateStarForceStats(currentStarLevel, item?.rarity || 'common'),
    consecutiveFailures
  }), [item, currentStarLevel, consecutiveFailures]);

  // Memoize enhancement info calculation
  const info = useMemo(() =>
    getEnhancementInfo(currentItem, useSafeguard),
    [currentItem, useSafeguard]
  );
  const starEssence = playerCharacter?.starEssence || 0;
  const canAfford = info && starEssence >= info.cost;

  // Reset safeguard when it's not available for current star level
  useEffect(() => {
    if (info && !info.safeguardAvailable && useSafeguard) {
      setUseSafeguard(false);
    }
  }, [info?.safeguardAvailable, useSafeguard]);

  // Sync star level and consecutive failures from prop on initial load or if item changes
  useEffect(() => {
    setCurrentStarLevel(item?.starForce || 0);
    setConsecutiveFailures(item?.consecutiveFailures || 0);
    setEnhanceResult(null);
  }, [item?.instanceId]);

  // Handle enhancement attempt
  const handleEnhance = useCallback(() => {
    if (!canAfford || isEnhancing || !info?.canEnhance) return;

    setIsEnhancing(true);
    setEnhanceResult(null);
    setAnimationPhase('charging');

    // Capture current safeguard state for this enhancement attempt
    const safeguardForThisAttempt = useSafeguard;

    // Charging phase (1s)
    setTimeout(() => {
      setAnimationPhase('flash');

      // Flash phase (0.3s)
      setTimeout(() => {
        // Perform enhancement with captured safeguard state
        const result = enhanceEquipment(item.instanceId, safeguardForThisAttempt);
        setEnhanceResult(result);
        setAnimationPhase('result');

        // Update local star level and consecutive failures from result
        if (result && typeof result.newStars === 'number') {
          setCurrentStarLevel(result.newStars);
          // Reset pity on success, increment on failure
          if (result.success) {
            setConsecutiveFailures(0);
          } else {
            setConsecutiveFailures(prev => prev + 1);
          }
        }

        // Allow retry after seeing result
        setTimeout(() => {
          setIsEnhancing(false);
          setAnimationPhase(null);
        }, 500);
      }, 300);
    }, 1000);
  }, [canAfford, isEnhancing, info, item.instanceId, useSafeguard, enhanceEquipment]);

  // Render stars
  const renderStars = (current, max = STAR_FORCE_CONFIG.maxStars) => {
    const stars = [];
    for (let i = 1; i <= max; i++) {
      const isFilled = i <= current;
      const isMilestone = i === 5 || i === 10 || i === 15;
      stars.push(
        <span
          key={i}
          className={`sf-star ${isFilled ? 'filled' : 'empty'} ${isMilestone ? 'milestone' : ''}`}
        >
          {isFilled ? '★' : '☆'}
        </span>
      );
      // Add separator after 5th and 10th star
      if (i === 5 || i === 10) {
        stars.push(<span key={`sep-${i}`} className="sf-star-sep">|</span>);
      }
    }
    return stars;
  };

  // Get result message
  const getResultMessage = () => {
    if (!enhanceResult) return null;

    if (enhanceResult.success) {
      return (
        <div className="sf-result success">
          <span className="sf-result-icon">✨</span>
          <span className="sf-result-text">
            Success! {enhanceResult.previousStars}★ → {enhanceResult.newStars}★
          </span>
        </div>
      );
    } else if (enhanceResult.result === 'fail_stay') {
      return (
        <div className="sf-result fail-stay">
          <span className="sf-result-icon">💫</span>
          <span className="sf-result-text">
            Failed ({enhanceResult.newStars}★)
          </span>
        </div>
      );
    } else if (enhanceResult.result === 'fail_downgrade') {
      return (
        <div className="sf-result fail-down">
          <span className="sf-result-icon">⬇️</span>
          <span className="sf-result-text">
            Failed! {enhanceResult.previousStars}★ → {enhanceResult.newStars}★
          </span>
        </div>
      );
    }
    return null;
  };

  if (!item || !info) return null;

  return (
    <div className="sf-overlay" onClick={onClose}>
      <div className="sf-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sf-header">
          <span className="sf-header-icon">⭐</span>
          <span className="sf-header-title">Star Force Enhancement</span>
          <button className="sf-close" onClick={onClose}>×</button>
        </div>

        {/* Item Preview */}
        <div className={`sf-item-preview ${animationPhase || ''}`}>
          <div className="sf-item-container">
            <ItemChip item={currentItem} size="large" showName={false} />
          </div>
          <div className="sf-star-display">
            {renderStars(currentStarLevel)}
          </div>
          <div className="sf-item-name">{item.name}</div>
        </div>

        {/* Enhancement Info */}
        {info?.isMaxed ? (
          <div className="sf-maxed">
            <span className="sf-maxed-icon">🌟</span>
            <span className="sf-maxed-text">Maximum Stars Reached!</span>
          </div>
        ) : (
          <>
            {/* Star Change Preview */}
            <div className="sf-change">
              <span className="sf-change-current">{currentStarLevel}★</span>
              <span className="sf-change-arrow">→</span>
              <span className="sf-change-target">{(info?.targetStars || currentStarLevel + 1)}★</span>
            </div>

            {/* Stat Changes */}
            {info?.preview?.statChanges && Object.keys(info.preview.statChanges).length > 0 && (
              <div className="sf-stats" key={`stats-${currentStarLevel}`}>
                <div className="sf-stats-label">Stat Changes</div>
                <div className="sf-stats-grid">
                  {Object.entries(info.preview.statChanges).map(([stat, value]) => (
                    <div key={stat} className="sf-stat-row">
                      <span className="sf-stat-name">
                        {stat === 'attack' && '⚔️ ATK'}
                        {stat === 'defense' && '🛡️ DEF'}
                        {stat === 'health' && '❤️ HP'}
                        {stat === 'critChance' && '💥 CRIT'}
                        {stat === 'critDamage' && '💢 C.DMG'}
                        {stat === 'allStats' && '📊 All Stats'}
                      </span>
                      <span className="sf-stat-value">
                        +{stat === 'critChance' || stat === 'critDamage' || stat === 'allStats'
                          ? `${Math.round(value * 100)}%`
                          : value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success Rate */}
            <div className="sf-rate">
              <div className="sf-rate-label">
                Success Rate: {info?.successRatePercent}%
                {info?.pityBonus > 0 && (
                  <span className="sf-pity-bonus">
                    (+{Math.round(info.pityBonus * 100)}% pity)
                  </span>
                )}
              </div>
              <div className="sf-rate-bar">
                <div
                  className="sf-rate-fill"
                  style={{ width: `${info?.successRatePercent || 0}%` }}
                />
              </div>
              <div className="sf-fail-info">
                {info?.failureBehavior === 'stay' && (
                  <span className="sf-fail-stay">On failure: Stars stay at {currentStarLevel}★</span>
                )}
                {info?.failureBehavior === 'downgrade' && (
                  <span className="sf-fail-down">On failure: Stars drop to {Math.max(5, currentStarLevel - 1)}★</span>
                )}
              </div>
            </div>

            {/* Cost */}
            <div className={`sf-cost ${canAfford ? 'can-afford' : 'cannot-afford'}`}>
              <span className="sf-cost-label">Cost:</span>
              <span className="sf-cost-value">
                <span className="sf-essence-icon">✦</span>
                {info?.cost || 0}
              </span>
              <span className="sf-cost-have">
                (You have: <span className="sf-essence-icon">✦</span>{starEssence})
              </span>
            </div>

            {/* Safeguard Option */}
            {info?.safeguardAvailable && (
              <label className="sf-safeguard">
                <input
                  type="checkbox"
                  checked={useSafeguard}
                  onChange={e => setUseSafeguard(e.target.checked)}
                  disabled={isEnhancing}
                />
                <span className="sf-safeguard-label">
                  Safeguard Mode (2× cost, prevents downgrade)
                </span>
              </label>
            )}

            {/* Result Message */}
            {getResultMessage()}

            {/* Enhance Button */}
            <div className="sf-actions">
              <button
                className={`sf-enhance-btn ${canAfford && !info?.isMaxed ? '' : 'disabled'} ${isEnhancing ? 'enhancing' : ''}`}
                onClick={handleEnhance}
                disabled={!canAfford || isEnhancing || info?.isMaxed}
              >
                <span className="sf-enhance-icon">⭐</span>
                {isEnhancing ? 'Enhancing...' : 'Enhance'}
                <span className="sf-enhance-icon">⭐</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default StarForceModal;
